import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import { VideoGallery } from '../types';
import { supabase } from '../lib/supabase';

interface VideoGalleryContextType {
  videos: VideoGallery[];
  loading: boolean;
  error: string | null;
  getVideo: (id: number) => VideoGallery | undefined;
  addVideo: (video: Omit<VideoGallery, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateVideo: (id: number, video: Partial<VideoGallery>) => Promise<void>;
  deleteVideo: (id: number) => Promise<void>;
  refreshVideos: () => Promise<void>;
  ensureLoaded: () => void;
}

const VideoGalleryContext = createContext<VideoGalleryContextType | undefined>(undefined);

export const VideoGalleryProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [videos, setVideos] = useState<VideoGallery[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchVideos = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('video_gallery')
        .select('*')
        .order('display_order', { ascending: true });

      if (fetchError) throw fetchError;

      setVideos(data || []);
    } catch (err) {
      console.error('Error fetching video gallery:', err);
      setError(err instanceof Error ? err.message : '영상 갤러리를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 지연 로드: 앱 부팅 시 모든 Provider가 전체 테이블을 읽으면 방문자 1명당 요청 수십 건이 발생해
  // 접속이 몰릴 때 DB가 포화된다. 이 데이터를 쓰는 화면이 뜰 때만 한 번 읽는다.
  const loadedRef = useRef(false);
  const ensureLoaded = useCallback(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    fetchVideos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getVideo = (id: number) => {
    return videos.find(video => video.id === id);
  };

  const addVideo = async (video: Omit<VideoGallery, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      setError(null);

      const { error: insertError } = await supabase
        .from('video_gallery')
        .insert([video]);

      if (insertError) throw insertError;

      await fetchVideos();
    } catch (err) {
      console.error('Error adding video:', err);
      setError(err instanceof Error ? err.message : '영상 추가에 실패했습니다.');
      throw err;
    }
  };

  const updateVideo = async (id: number, video: Partial<VideoGallery>) => {
    try {
      setError(null);

      const { error: updateError } = await supabase
        .from('video_gallery')
        .update(video)
        .eq('id', id);

      if (updateError) throw updateError;

      await fetchVideos();
    } catch (err) {
      console.error('Error updating video:', err);
      setError(err instanceof Error ? err.message : '영상 수정에 실패했습니다.');
      throw err;
    }
  };

  const deleteVideo = async (id: number) => {
    try {
      setError(null);

      const { error: deleteError } = await supabase
        .from('video_gallery')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      await fetchVideos();
    } catch (err) {
      console.error('Error deleting video:', err);
      setError(err instanceof Error ? err.message : '영상 삭제에 실패했습니다.');
      throw err;
    }
  };

  const refreshVideos = async () => {
    await fetchVideos();
  };

  return (
    <VideoGalleryContext.Provider value={{
      ensureLoaded,
      videos,
      loading,
      error,
      getVideo,
      addVideo,
      updateVideo,
      deleteVideo,
      refreshVideos
    }}>
      {children}
    </VideoGalleryContext.Provider>
  );
};

export const useVideoGallery = () => {
  const context = useContext(VideoGalleryContext);
  // 이 훅을 쓰는 화면이 실제로 떴을 때만 조회를 시작한다(지연 로드).
  const ensureLoaded = context?.ensureLoaded;
  useEffect(() => {
    ensureLoaded?.();
  }, [ensureLoaded]);

  if (context === undefined) {
    throw new Error('useVideoGallery must be used within a VideoGalleryProvider');
  }
  return context;
};
